import React from 'react'
import { FormattedMessage } from 'react-intl'
import { accentuate, codify } from '../'
import { Section, IconMessage, Heading, InlineCode, Subheading, UnorderedList, ExternalLinkBlock } from '../s'
import Divider from 'components/divider'
import Paragraph from 'components/typography/paragraph'
import { ListItem } from 'components/list'
import { AlertTriangleFilled as Warning } from 'components/feather'

const WebhookHowToUse = ({ data }) => (
    <Section>
        <Heading>
            <FormattedMessage
                id="integration_heading_how_to_use"
                defaultMessage="How to use" />
        </Heading>
        <Paragraph>
            <FormattedMessage
                id="integration_webhook_preamble_how_to_use_1"
                defaultMessage="To get started, simply provide a url which can receive the requests the webhook will send. You can also optionally register a client secret and/or an access token." />
        </Paragraph>
        <UnorderedList>
            <ListItem>
                <FormattedMessage
                    id="integration_webhook_preamble_client_secret"
                    defaultMessage="If the <strong>client secret</strong> is set, each request will include a header <code>huma-hmac-sha256</code> with a hash of the json body, signed with the secret to verify the message."
                    values={{
                        code: codify,
                        strong: accentuate
                    }} />
            </ListItem>
            <ListItem>
                <FormattedMessage
                    id="integration_webhook_preamble_access_token"
                    defaultMessage="If the <strong>access token</strong> is set, each request will include it as a standard bearer token."
                    values={{
                        code: codify,
                        strong: accentuate
                    }} />
            </ListItem>
        </UnorderedList>
        <Paragraph>
            <FormattedMessage
                id="integration_webhook_preamble_how_to_use_2"
                defaultMessage="All requests include a <code>huma-topic</code> header, as well as a json body containing a <code>topic</code> property. The value of header <code>huma-topic</code> and body property <code>topic</code> will be identical, and will be one of:"
                values={{ code: codify }} />
        </Paragraph>
        <Subheading>
            <FormattedMessage
                id="integration_webhook_preamble_topics_profile"
                defaultMessage="For profile data, the topic will be one of:" />
        </Subheading>
        <UnorderedList>
            <ListItem>
                <InlineCode>users-create</InlineCode>
            </ListItem>
            <ListItem>
                <InlineCode>users-update</InlineCode>
            </ListItem>
            <ListItem>
                <InlineCode>users-delete</InlineCode>
            </ListItem>
            <ListItem>
                <InlineCode>users-export</InlineCode>
            </ListItem>
        </UnorderedList>
        <Subheading>
            <FormattedMessage
                id="integration_webhook_preamble_topics_groups"
                defaultMessage="For team and location data, the topic will be one of:" />
        </Subheading>
        <UnorderedList>
            <ListItem>
                <InlineCode>groups-create</InlineCode>
            </ListItem>
            <ListItem>
                <InlineCode>groups-update</InlineCode>
            </ListItem>
            <ListItem>
                <InlineCode>groups-delete</InlineCode>
            </ListItem>
            <ListItem>
                <InlineCode>groups-export</InlineCode>
            </ListItem>
            <ListItem>
                <InlineCode>groups-add-members</InlineCode>
            </ListItem>
            <ListItem>
                <InlineCode>groups-remove-members</InlineCode>
            </ListItem>
        </UnorderedList>
        <Subheading>
            <FormattedMessage
                id="integration_webhook_preamble_topics_absence"
                defaultMessage="For absence data, the topic will be one of:" />
        </Subheading>
        <UnorderedList>
            <ListItem>
                <InlineCode>absence-create</InlineCode>
            </ListItem>
            <ListItem>
                <InlineCode>absence-update</InlineCode>
            </ListItem>
            <ListItem>
                <InlineCode>absence-delete</InlineCode>
            </ListItem>
        </UnorderedList>

        <Divider />

        <IconMessage className="warning">
            <Warning size={20} />
            <div>
                <Paragraph className="compact">
                    <FormattedMessage
                        id="integration_webhook_message_request_handled"
                        defaultMessage="Be aware, if a request is not handled within 1 second, it will time out, so it may be useful to persist received data and respond, then do any further work to the data in a separate process." />
                </Paragraph>
            </div>
        </IconMessage>

        <Divider />

        <ExternalLinkBlock href={data.knowledgebaseUrl}>
            <FormattedMessage
                id="integration_webhook_link_knowledgebase"
                defaultMessage="Using the Webhook" />
        </ExternalLinkBlock>
    </Section>
)

export default WebhookHowToUse